import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import BuyCreditsModal from "../components/BuyCreditsModal";
import { useAuth } from "../hooks/useAuth";
import { apiService } from "../services/api";
import { appBackendErrorOf } from "../services/appBackend";
import {
  getCreditPacks,
  getProducts,
  isBillingAvailable,
} from "../services/billingApi";
import { ProductKey } from "../types/billing";
import {
  BillingState,
  BuyCreditsPreset,
  PacksState,
  ProductsState,
  WalletBalance,
  WalletContextValue,
} from "../types/wallet";
import {
  FALLBACK_PRODUCTS,
  UnreadableCatalogueError,
} from "../utils/productCatalogue";
import { saveProductDraft } from "../utils/productDraft";
import { addBeforeSignInRedirect } from "../services/session";
import { takePurchaseInFlight } from "../utils/purchaseMarker";

/**
 * The wallet, the catalogue and the Buy Credits dialog, shared by every signed-in page.
 *
 * One provider around the authenticated routes rather than state in the Header: each page
 * renders its own Header, so anything kept there would be fetched again on every navigation.
 * Here the balance, the packs and the products are asked for once per visit, and refreshed only
 * when something has changed them.
 *
 * Nothing is asked for until the user is signed in. `apiService` wipes the session and sends the
 * browser to "/" on any 401, so a wallet call made while the sign-in is still settling would
 * throw the user out of a session that was about to work.
 */

/**
 * When to look at the balance after a purchase, from the moment the app is back. The Credits
 * land when Stripe's webhook reaches the backend, usually within seconds, sometimes later; the
 * first look that finds more Credits than before ends the wait.
 */
export const PURCHASE_REFRESH_DELAYS_MS = [0, 2000, 4000, 8000, 16000];

const fallbackProducts = (): ProductsState => ({
  status: "ready",
  products: FALLBACK_PRODUCTS.map((product) => ({ ...product })),
  fromBackend: false,
});

const billingStateOf = (
  packs: PacksState,
  products: ProductsState,
): BillingState => {
  if (packs.status === "loading" || products.status === "loading") {
    return "loading";
  }
  const anyProduct = products.products.some((product) => product.purchase);
  return packs.status === "ready" || anyProduct ? "ready" : "unavailable";
};

/**
 * What a page sees when it is rendered outside the provider — in a test, or anywhere that is
 * not behind sign-in. Nothing loads and nothing can be bought, and every call is a no-op, so a
 * page that reads the wallet renders as it would where billing does not exist.
 */
const OUTSIDE_PROVIDER: WalletContextValue = {
  balance: { status: "unavailable" },
  walletChecked: false,
  refresh: async () => null,
  refreshAfterPurchase: async () => undefined,
  catalogue: {
    packs: { status: "unavailable" },
    products: fallbackProducts(),
    billing: "unavailable",
  },
  reloadCatalogue: async () => undefined,
  openBuyCredits: () => undefined,
  closeBuyCredits: () => undefined,
  registerDraft: () => () => undefined,
};

const WalletContext = createContext<WalletContextValue>(OUTSIDE_PROVIDER);

export const useWallet = (): WalletContextValue => useContext(WalletContext);

interface WalletProviderProps {
  children: ReactNode;
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  const [balance, setBalance] = useState<WalletBalance>({ status: "loading" });
  const [walletChecked, setWalletChecked] = useState(false);
  // Known from the first render where there is no app-backend at all, so that case never shows
  // a loading state it will not leave.
  const [packs, setPacks] = useState<PacksState>(() =>
    isBillingAvailable() ? { status: "loading" } : { status: "unavailable" },
  );
  const [products, setProducts] = useState<ProductsState>(() =>
    isBillingAvailable() ? { status: "loading" } : fallbackProducts(),
  );
  const [preset, setPreset] = useState<BuyCreditsPreset | null>(null);
  // Bumped on each open, so opening again with another preset starts the dialog afresh.
  const [openCount, setOpenCount] = useState(0);

  // Whether it is still worth calling setState: the calls outlive a render on purpose.
  const mounted = useRef(true);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  // Only the newest call of each kind may write its answer: an older one that comes back later
  // would otherwise put an older number over a newer one.
  const balanceCall = useRef(0);
  const packsCall = useRef(0);
  const productsCall = useRef(0);
  // The last balance read, for the refresh that fails to keep and for the purchase wait to
  // compare against.
  const knownCredits = useRef<number | null>(null);
  const purchaseWait = useRef(0);
  // How to read each product page's unfinished input, for the pages on screen. A ref: a page
  // coming or going changes nothing on screen, and must not re-render every page that reads
  // the wallet.
  const draftCaptures = useRef(new Map<ProductKey, () => unknown>());
  const started = useRef(false);
  const signedIn = useRef(isAuthenticated);
  signedIn.current = isAuthenticated;

  useEffect(() => {
    mounted.current = true;
    const pending = timers.current;
    return () => {
      mounted.current = false;
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, []);

  const wait = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        const timer = setTimeout(() => {
          timers.current.delete(timer);
          resolve();
        }, ms);
        timers.current.add(timer);
      }),
    [],
  );

  const refresh = useCallback(async (): Promise<number | null> => {
    const call = ++balanceCall.current;
    try {
      const wallet = await apiService.getWalletBalance();
      // A shape check rather than a null check: a 200 from something that is not the dashboard
      // API resolves with a body that has no balance in it at all.
      const credits = wallet?.balance;
      if (typeof credits !== "number" || !Number.isFinite(credits)) {
        throw new Error("the wallet answered without a balance");
      }
      if (mounted.current && call === balanceCall.current) {
        knownCredits.current = credits;
        setBalance({ status: "known", credits });
      }
      return credits;
    } catch (error) {
      console.error("Failed to fetch wallet balance:", error);
      if (
        mounted.current &&
        call === balanceCall.current &&
        knownCredits.current === null
      ) {
        setBalance({ status: "unavailable" });
      }
      return null;
    } finally {
      if (mounted.current) setWalletChecked(true);
    }
  }, []);

  const refreshAfterPurchase = useCallback(async (): Promise<void> => {
    // A second wait replaces the first rather than running beside it.
    const run = ++purchaseWait.current;
    let before = knownCredits.current;
    for (const delay of PURCHASE_REFRESH_DELAYS_MS) {
      if (delay > 0) await wait(delay);
      if (!mounted.current || run !== purchaseWait.current) return;
      const credits = await refresh();
      if (credits === null) continue;
      // Straight back from Stripe nothing is known yet, so the first answer is the number to
      // beat. If the Credits had already landed by then, the wait simply runs out.
      if (before === null) {
        before = credits;
      } else if (credits > before) {
        return;
      }
    }
  }, [refresh, wait]);

  const loadPacks = useCallback(async (): Promise<void> => {
    if (!isBillingAvailable()) {
      setPacks({ status: "unavailable" });
      return;
    }
    const call = ++packsCall.current;
    // A reload leaves the current list on screen until the new one arrives.
    try {
      const loaded = await getCreditPacks();
      if (!mounted.current || call !== packsCall.current) return;
      setPacks(
        loaded.length > 0
          ? { status: "ready", packs: loaded }
          : { status: "unavailable" },
      );
    } catch (error) {
      console.error("Failed to load credit packs:", appBackendErrorOf(error));
      if (mounted.current && call === packsCall.current) {
        setPacks({ status: "unavailable" });
      }
    }
  }, []);

  const loadProducts = useCallback(async (): Promise<void> => {
    if (!isBillingAvailable()) {
      setProducts(fallbackProducts());
      return;
    }
    const call = ++productsCall.current;
    try {
      const loaded = await getProducts();
      if (!mounted.current || call !== productsCall.current) return;
      setProducts({ status: "ready", products: loaded, fromBackend: true });
    } catch (error) {
      console.error(
        "Failed to load products:",
        error instanceof UnreadableCatalogueError
          ? error.message
          : appBackendErrorOf(error),
      );
      // Run costs are still worth showing, and the fallback's products cannot be bought, so a
      // purchase that was on offer a moment ago is withdrawn rather than left on stale terms.
      if (mounted.current && call === productsCall.current) {
        setProducts(fallbackProducts());
      }
    }
  }, []);

  const reloadCatalogue = useCallback(async (): Promise<void> => {
    await Promise.all([loadPacks(), loadProducts()]);
  }, [loadPacks, loadProducts]);

  useEffect(() => {
    if (!isAuthenticated || started.current) return;
    started.current = true;

    // Back from Stripe: the note left before the redirect says a purchase is in flight, so the
    // balance is watched until the Credits land rather than read once.
    if (takePurchaseInFlight()) {
      refreshAfterPurchase();
    } else {
      refresh();
    }
    loadPacks();
    loadProducts();
  }, [isAuthenticated, refresh, refreshAfterPurchase, loadPacks, loadProducts]);

  useEffect(() => {
    // Back from Stripe's page can also restore this app from the browser's back-forward cache
    // exactly as it left it, with no reload to read the note. The purchase is most likely
    // abandoned, but it may have gone through before the buyer came back, so look again.
    // The page restored is the draft itself, so there is nothing for it to restore.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || !signedIn.current) return;
      if (takePurchaseInFlight()) refreshAfterPurchase();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [refreshAfterPurchase]);

  const openBuyCredits = useCallback((next: BuyCreditsPreset) => {
    // Opened with no page to come back to (the header's Top up) while a product page with input
    // in progress is on screen: that page is the one to come back to, and its input goes along
    // to Stripe like any other purchase's. A page with nothing in progress (a past run, the edit
    // form) is not adopted, or "Back to {Product}" would lead somewhere the buyer never was.
    // Only one product page is mounted at a time; were there ever more, none is guessed at.
    let returnProduct = next.returnProduct;
    const onScreen = Array.from(draftCaptures.current.entries());
    if (returnProduct === undefined && onScreen.length === 1) {
      const [product, capture] = onScreen[0];
      let draft: unknown = null;
      try {
        draft = capture();
      } catch {
        draft = null;
      }
      if (draft !== null && draft !== undefined) returnProduct = product;
    }
    setPreset(
      returnProduct === next.returnProduct ? next : { ...next, returnProduct },
    );
    setOpenCount((count) => count + 1);
  }, []);

  const closeBuyCredits = useCallback(() => setPreset(null), []);

  // A session that ended and has to be signed in again on this page (the popup could not open)
  // leaves the page like a trip to Stripe does: what the open product page holds is kept for when
  // the sign-in brings the user back to it.
  useEffect(
    () =>
      addBeforeSignInRedirect(() => {
        draftCaptures.current.forEach((capture, product) => {
          const draft = capture();
          if (draft !== null && draft !== undefined) {
            saveProductDraft(product, draft);
          }
        });
      }),
    [],
  );

  const registerDraft = useCallback(
    (product: ProductKey, capture: () => unknown): (() => void) => {
      draftCaptures.current.set(product, capture);
      return () => {
        // Only its own: a page that mounts again registers before the old one lets go.
        if (draftCaptures.current.get(product) === capture) {
          draftCaptures.current.delete(product);
        }
      };
    },
    [],
  );

  // The dialog is about to send the browser to Stripe for a purchase started from `product`.
  // Whatever that product's page has in progress is kept for when the buyer comes back; from
  // anywhere else (the landing page, the transactions) there is no page on screen to keep.
  const keepDraftFor = useCallback((product: ProductKey | null) => {
    if (!product) return;
    const capture = draftCaptures.current.get(product);
    if (!capture) return;
    try {
      const draft = capture();
      if (draft !== null && draft !== undefined) {
        saveProductDraft(product, draft);
      }
    } catch (error) {
      // The purchase matters more than the draft; it goes ahead without one.
      console.error("Failed to keep the page's draft:", error);
    }
  }, []);

  const catalogue = useMemo(
    () => ({ packs, products, billing: billingStateOf(packs, products) }),
    [packs, products],
  );

  const value = useMemo<WalletContextValue>(
    () => ({
      balance,
      walletChecked,
      refresh,
      refreshAfterPurchase,
      catalogue,
      reloadCatalogue,
      openBuyCredits,
      closeBuyCredits,
      registerDraft,
    }),
    [
      balance,
      walletChecked,
      refresh,
      refreshAfterPurchase,
      catalogue,
      reloadCatalogue,
      openBuyCredits,
      closeBuyCredits,
      registerDraft,
    ],
  );

  return (
    <WalletContext.Provider value={value}>
      {children}
      {preset && (
        <BuyCreditsModal
          key={openCount}
          preset={preset}
          balance={balance}
          walletChecked={walletChecked}
          catalogue={catalogue}
          onCatalogueStale={reloadCatalogue}
          onLeavingForCheckout={keepDraftFor}
          onClose={closeBuyCredits}
        />
      )}
    </WalletContext.Provider>
  );
};

export default WalletContext;
